// Programmatic SEO content for /paardenrassen/, /aandoeningen/, /entingen/,
// /ontworming/ and /disciplines/ pages.
// Same shape as KENNISBANK so we can reuse the same renderer.

export const PAARDENRASSEN = {
  "fries-paard": {
    slug: "fries-paard",
    category: "Paardenrassen",
    title: "Fries paard — kenmerken, geschiedenis & verzorging",
    lead:
      "Het Friese paard is een van de oudste Europese paardenrassen, herkenbaar aan zijn zwarte vacht, lange manen en hoge knieactie. In deze gids: rasstandaard, karakter, gezondheidspunten en EquiSight tips.",
    datePublished: "2026-05-01",
    sections: [
      { h: "Kenmerken", list: ["Zwarte vacht (uitsluitend)", "Lange manen en staart", "Lange behang", "Stokmaat: 1.55–1.70m", "Hoge knieactie"] },
      { h: "Karakter", p: "Vriendelijk, leergierig en mensgericht. Geschikt voor dressuur, mensport en recreatie." },
      { h: "Gezondheidspunten", list: ["Slokdarmverstopping (choke) — risico op hooi", "Megaslokdarm", "PSSM type 1 (niet vaak, maar test bij twijfel)"] },
    ],
    related: ["kwpn"],
  },
  kwpn: {
    slug: "kwpn",
    category: "Paardenrassen",
    title: "KWPN — Nederlands Warmbloed paard",
    lead:
      "Het KWPN (Koninklijk Warmbloed Paardenstamboek Nederland) heeft een uitzonderlijke staat van dienst in dressuur en springen. Wereldtop in beide disciplines.",
    datePublished: "2026-05-02",
    sections: [
      { h: "Kenmerken", list: ["Stokmaat: 1.60–1.75m", "Atletisch", "Goed karakter", "Geschikt voor topsport"] },
      { h: "Stamboek", p: "Het KWPN voert strenge selectie op gangen, springvermogen, exterieur en karakter." },
    ],
    related: ["fries-paard"],
  },
  "arabisch-volbloed": {
    slug: "arabisch-volbloed",
    category: "Paardenrassen",
    title: "Arabisch Volbloed paard — kenmerken & verzorging",
    lead:
      "Het Arabisch Volbloed staat bekend om uithoudingsvermogen, intelligentie en het kenmerkende fijne hoofd. Ideaal voor endurance.",
    datePublished: "2026-05-03",
    sections: [
      { h: "Kenmerken", list: ["Stokmaat: 1.45–1.55m", "Fijn hoofd, concave neus", "Hoge staartdracht", "Uithoudingsvermogen"] },
      { h: "Geschikt voor", p: "Endurance, recreatie, showing." },
    ],
    related: ["kwpn"],
  },
  "shetland-pony": {
    slug: "shetland-pony",
    category: "Paardenrassen",
    title: "Shetland pony — kenmerken & valkuilen",
    lead:
      "De Shetlander is klein maar sterk. Pas op met voeding — hoefbevangenheid is een groot risico.",
    datePublished: "2026-05-04",
    sections: [
      { h: "Kenmerken", list: ["Stokmaat: <1.07m", "Sterk gebouwd", "Lang behang"] },
      { h: "Voedingsvalkuil", p: "Te weelderige weide leidt snel tot hoefbevangenheid. Beperk weidegang, gebruik graskorrel of muilkorf indien nodig." },
    ],
    related: ["haflinger"],
  },
  haflinger: {
    slug: "haflinger",
    category: "Paardenrassen",
    title: "Haflinger paard — kenmerken & gebruik",
    lead:
      "De Haflinger komt uit de Alpen en is herkenbaar aan zijn vosbruine vacht met witte manen. Veelzijdig in recreatie, mensport en lichte dressuur.",
    datePublished: "2026-05-05",
    sections: [
      { h: "Kenmerken", list: ["Stokmaat: 1.38–1.52m", "Vos met witte manen", "Sterk en gezond"] },
    ],
    related: ["shetland-pony"],
  },
};

export const AANDOENINGEN = {
  koliek: {
    slug: "koliek",
    category: "Aandoeningen",
    title: "Koliek paard — symptomen & wat te doen",
    lead: "Koliek is buikpijn bij paarden en kan levensbedreigend zijn. Bel altijd direct de dierenarts bij twijfel.",
    datePublished: "2026-05-01",
    sections: [
      { h: "Symptomen", list: ["Niet eten", "Rusteloos, krabben", "Liggen en opstaan", "Naar de buik kijken", "Zweten"] },
      { h: "Eerste hulp", p: "Bel de dierenarts. Voorkom rollen indien zwaar. Wandel rustig. Geen voer of water." },
    ],
    related: ["hoefbevangenheid"],
  },
  hoefbevangenheid: {
    slug: "hoefbevangenheid",
    category: "Aandoeningen",
    title: "Hoefbevangenheid (laminitis) — preventie & behandeling",
    lead:
      "Hoefbevangenheid is een ontsteking in de lamellen van de hoef. Vaak veroorzaakt door verkeerde voeding, te veel fructaan of metabole problemen.",
    datePublished: "2026-05-02",
    sections: [
      { h: "Symptomen", list: ["Stijve, voorzichtige gang", "Voorbeen vooruit", "Warmte in de hoef", "Pulserende slagader", "Stappen op een trampoline"] },
      { h: "Preventie", list: ["Geen overgewicht", "Voorzichtig met gras (fructaan)", "Regelmatig bekappen", "Endocriene controle bij risicopaarden"] },
    ],
    related: ["koliek"],
  },
  droes: {
    slug: "droes",
    category: "Aandoeningen",
    title: "Droes (strangles) — herkennen & isoleren",
    lead: "Droes is zeer besmettelijk. Snelle herkenning en isolatie zijn cruciaal.",
    datePublished: "2026-05-03",
    sections: [
      { h: "Symptomen", list: ["Koorts", "Zwellingen onder kaak", "Etterende neus", "Geen eetlust"] },
      { h: "Isolatie", p: "Quarantaine, aparte materialen, hygiëne, screening andere paarden." },
    ],
    related: ["koliek"],
  },
  "west-nijl": {
    slug: "west-nijl",
    category: "Aandoeningen",
    title: "West-Nijl virus paard — vaccinatie & symptomen",
    lead:
      "West-Nijl is een door muggen overgebrachte virusinfectie. Vaccinatie is in risicogebieden sterk aanbevolen.",
    datePublished: "2026-05-04",
    sections: [
      { h: "Symptomen", list: ["Koorts", "Spiertrillingen", "Coördinatieproblemen", "Verlamming"] },
      { h: "Vaccinatie", p: "Jaarlijks, vóór muggentijd. Eerst basisserie van 2 vaccinaties." },
    ],
    related: ["rhinopneumonie"],
  },
  rhinopneumonie: {
    slug: "rhinopneumonie",
    category: "Aandoeningen",
    title: "Rhinopneumonie paard — vaccinatie & uitbraak",
    lead:
      "Rhinopneumonie (EHV-1/4) veroorzaakt luchtwegproblemen, verwerpen bij drachtige merries en zelden neurologische klachten.",
    datePublished: "2026-05-05",
    sections: [
      { h: "Symptomen", list: ["Hoesten en neusuitvloeiing", "Koorts", "Verwerpen", "In zeldzame gevallen neurologisch"] },
      { h: "Vaccinatie", p: "Iedere 6 maanden, vooral op grote stallen en bij drachtige merries." },
    ],
    related: ["west-nijl"],
  },
  zomereczeem: {
    slug: "zomereczeem",
    category: "Aandoeningen",
    title: "Zomereczeem paard — preventie & behandeling",
    lead: "Zomereczeem is allergie voor kriebelmug speeksel. Preventie is alles.",
    datePublished: "2026-05-06",
    sections: [
      { h: "Symptomen", list: ["Jeuk manenkam, staartwortel", "Korstjes", "Kale plekken"] },
      { h: "Preventie", list: ["Eczeemdeken", "Op piekuren binnen", "Insectenwerend middel", "Ventilator"] },
    ],
    related: ["droes"],
  },
};

export const ENTINGEN_DETAIL = {
  "influenza-paard": {
    slug: "influenza-paard",
    category: "Entingen",
    title: "Influenza paard — vaccinatieschema",
    lead:
      "Paardeninfluenza is besmettelijk en KNHS verplicht een geldige vaccinatie voor wedstrijden.",
    datePublished: "2026-05-01",
    sections: [
      { h: "Basisserie", p: "Week 0 — vaccinatie 1, na 4 weken — vaccinatie 2, na ~6 maanden — vaccinatie 3." },
      { h: "Onderhoud", p: "Jaarlijks herhalen. Voor KNHS wedstrijden max 6 maanden geleden." },
    ],
    related: ["tetanus-paard"],
  },
  "tetanus-paard": {
    slug: "tetanus-paard",
    category: "Entingen",
    title: "Tetanus paard — vaccinatie & herhaling",
    lead: "Tetanus is altijd dodelijk zonder behandeling. Vaccinatie is sterk aangeraden.",
    datePublished: "2026-05-02",
    sections: [
      { h: "Basis", p: "Twee vaccinaties met 4 weken tussenruimte." },
      { h: "Onderhoud", p: "Iedere 2–3 jaar herhalen." },
    ],
    related: ["influenza-paard"],
  },
  "rhinopneumonie-paard": {
    slug: "rhinopneumonie-paard",
    category: "Entingen",
    title: "Rhinopneumonie vaccinatie paard",
    lead: "Vaccinatie tegen EHV-1/4 — vooral op grote stallen en bij drachtige merries.",
    datePublished: "2026-05-03",
    sections: [
      { h: "Schema", p: "Iedere 6 maanden. Drachtige merries op 5, 7 en 9 maanden dracht." },
    ],
    related: ["influenza-paard"],
  },
  "west-nijl-paard": {
    slug: "west-nijl-paard",
    category: "Entingen",
    title: "West-Nijl vaccinatie paard",
    lead: "Aanbevolen in risicogebieden voor muggentijd.",
    datePublished: "2026-05-04",
    sections: [{ h: "Schema", p: "Basisserie van 2 vaccinaties, daarna jaarlijks vóór de muggentijd." }],
    related: ["influenza-paard"],
  },
};

export const ONTWORMING_DETAIL = {
  "schema-paard": {
    slug: "schema-paard",
    category: "Ontworming",
    title: "Ontwormingsschema paard — slim & op basis van EPG",
    lead:
      "Een modern ontwormingsschema gaat uit van EPG-tests, niet blind ontwormen. Zo voorkom je resistentie en bespaar je geld.",
    datePublished: "2026-05-01",
    sections: [
      { h: "Wat is EPG?", p: "EPG (eitjes per gram) is een mestonderzoek dat laat zien hoeveel wormeitjes je paard uitscheidt. Onder de drempel? Niet ontwormen." },
      { h: "Schema-voorbeeld", list: ["Lente: EPG-test, alleen ontwormen bij hoge uitscheiding", "Zomer: EPG-test", "Najaar: Generieke ontworming (lintworm + maagdaslarven)", "Winter: rust"] },
    ],
    related: ["epg-paard"],
  },
  "epg-paard": {
    slug: "epg-paard",
    category: "Ontworming",
    title: "EPG test paard — eitjes per gram meten",
    lead: "EPG-tests vertellen je of ontworming echt nodig is. Selectief ontwormen voorkomt resistentie.",
    datePublished: "2026-05-02",
    sections: [
      { h: "Hoe werkt het?", p: "Vers mestmonster naar het lab. Resultaat na enkele dagen." },
      { h: "Drempelwaarden", list: ["<200 EPG — laag, geen actie", "200–500 EPG — overweeg", ">500 EPG — ontwormen"] },
    ],
    related: ["schema-paard"],
  },
};

export const DISCIPLINES = {
  dressuur: {
    slug: "dressuur",
    category: "Discipline",
    title: "Dressuur — app, protocol & training",
    lead: "Dressuur draait om precisie, harmonie en consistente verbetering. EquiSight helpt je met protocolanalyse en doelen.",
    datePublished: "2026-05-01",
    sections: [
      { h: "Wat doet EquiSight?", list: ["KNHS en FEI protocol scannen", "Scores per onderdeel", "Trends EPI", "Trainingsdoelen"] },
    ],
    related: ["springen"],
  },
  springen: {
    slug: "springen",
    category: "Discipline",
    title: "Springen — app & planning",
    lead: "Springruiters plannen wedstrijden, registreren rondes en koppelen training aan resultaat.",
    datePublished: "2026-05-02",
    sections: [{ h: "Functies", list: ["Wedstrijdplanning", "Startlijst meldingen", "Rondes registreren", "Trainingsanalyse"] }],
    related: ["dressuur"],
  },
  eventing: {
    slug: "eventing",
    category: "Discipline",
    title: "Eventing — app & planning",
    lead: "Eventing combineert dressuur, cross en springen. EquiSight maakt planning eenvoudig.",
    datePublished: "2026-05-03",
    sections: [{ h: "Functies", list: ["Planning per onderdeel", "Conditioneringstrainings", "Cross-recces in agenda"] }],
    related: ["dressuur", "springen"],
  },
  endurance: {
    slug: "endurance",
    category: "Discipline",
    title: "Endurance — app & training",
    lead: "Voor endurance ruiters: trainingsplanning, voeding, hartslag, hersteltijden en logboek.",
    datePublished: "2026-05-04",
    sections: [{ h: "Functies", list: ["Trainingsplanning", "Voeding tijdens duurrit", "Hartslag en herstel", "Logboek per rit"] }],
    related: ["dressuur"],
  },
  western: {
    slug: "western",
    category: "Discipline",
    title: "Western — app & training",
    lead: "Voor reining, trail, pleasure en andere western disciplines: training, gezondheid en wedstrijden bijhouden.",
    datePublished: "2026-05-05",
    sections: [{ h: "Functies", list: ["Training per discipline", "Wedstrijdregistratie", "Gezondheidsdossier"] }],
    related: ["springen"],
  },
};

export default { PAARDENRASSEN, AANDOENINGEN, ENTINGEN_DETAIL, ONTWORMING_DETAIL, DISCIPLINES };
